import { useState } from 'react';
import { AIU_DEFINITION } from '../metricInfo';
import { Term } from './InfoTooltip';
import { ChartCard } from './ChartCard';
import { Modal } from './Modal';
import type { PremiumQuotaByModelPoint } from '@ace-tracker/shared';

const TOP_N = 5;

export function IndicativeCostChart({
  byModel,
  currency,
  indicativeCostPerAiu,
  formatCurrency,
  info,
  className,
}: {
  byModel: PremiumQuotaByModelPoint[];
  currency: string;
  indicativeCostPerAiu: number;
  formatCurrency: (value: number, currency: string) => string;
  info?: string;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sorted = [...byModel].sort((a, b) => b.indicativeCost - a.indicativeCost);
  const max = Math.max(...sorted.map((m) => m.indicativeCost), 0.01);
  const visible = sorted.slice(0, TOP_N);
  const hasMore = sorted.length > TOP_N;

  return (
    <>
      <ChartCard
        title="Legacy AIU estimate by model"
        caption={`Recorded AIU at ${formatCurrency(indicativeCostPerAiu, currency)} each — not GitHub AI-credit billing`}
        info={info}
        height="h-96"
        className={className}
        actions={
          hasMore && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-label={`Show all ${sorted.length} models`}
              className="flex h-11 w-11 items-center justify-center rounded-input text-text-muted hover:bg-surface-alt hover:text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M9 3H3v6M15 21h6v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )
        }
      >
        <IndicativeCostRows models={visible} max={max} currency={currency} formatCurrency={formatCurrency} />
        {hasMore && (
          <p className="mt-2 text-center text-[11px] text-text-faint">
            Showing top {TOP_N} of {sorted.length} models
          </p>
        )}
      </ChartCard>

      <Modal open={isExpanded} onClose={() => setIsExpanded(false)} title="Legacy AIU estimate by model">
        <IndicativeCostRows models={sorted} max={max} currency={currency} formatCurrency={formatCurrency} />
      </Modal>
    </>
  );
}

function IndicativeCostRows({
  models,
  max,
  currency,
  formatCurrency,
}: {
  models: PremiumQuotaByModelPoint[];
  max: number;
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
}) {
  return (
    <div className="grid gap-2.5">
      {models.map((m) => (
        <div key={m.model} className="grid grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-1 text-sm">
          <span className="truncate font-medium text-text-default">{m.model}</span>
          <span className="text-xs tabular-nums text-text-muted">
            {formatCurrency(m.indicativeCost, currency)} · {m.aiu.toFixed(2)} <Term title={AIU_DEFINITION}>AIU</Term>
          </span>
          <div className="col-span-2 h-[7px] overflow-hidden rounded-full border border-border bg-surface-alt">
            <div
              className="h-full rounded-full bg-primary-blue"
              style={{ width: `${Math.max((m.indicativeCost / max) * 100, 2)}%` }}
            />
          </div>
        </div>
      ))}
      {models.length === 0 && <p className="text-sm text-text-muted">No premium quota usage to estimate in this range.</p>}
    </div>
  );
}
